/**
 * ReportAnalysisTab Component
 *
 * Provides deep-dive analysis into a specific safety report/event.
 * Shows the report's pathway through the fault tree with visual highlighting.
 *
 * Layout: 66.67% fault tree dendrogram (left) / 33.33% report details (right)
 */

import React, {useCallback, useEffect, useState} from 'react';
import {Alert, Box, Button, ButtonGroup, Chip, Skeleton, Stack, Tooltip, Typography} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import EditIcon from '@mui/icons-material/Edit';
import ListAltIcon from '@mui/icons-material/ListAlt';
import {
    useIRPWorkbookStore, useIRPWorkbookStoreApi, useReportTabCache, useSelectedReportId
} from '../../../../../../../stores/irp-workbook.store';
import {IrpDescriptorTermDTO} from '../../../../../../../@types/integrated-risk-picture';
import HighlightedNarrative from '../../../../../../common/HighlightedNarrative.component';
import LocationWidget from '../LocationWidget';
import ReportMappingDendrogram from '../ReportMappingDendrogram';
import EventSequenceTreeMappingViewer from '../../../EventSequenceTreeMappingViewer';
import irpService from '../../../../../../../services/irp.service';
import {useIRPContext} from '../../IRPWorkbookProvider';
import {toast} from 'react-toastify';
import StrictDeleteModal from '../../../../../../form-components/StrictDeleteModal.component';
import {ResourceType} from '../../../../../../../@types/resource-type';

/**
 * Format date for display
 */
const formatDate = (dateValue: Date | string | null | undefined): string => {
    if (!dateValue) return 'N/A';
    try {
        const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    } catch {
        return 'N/A';
    }
};

/**
 * Report Analysis tab for individual event investigation
 */
type ViewMode = 'analyst' | 'editor';

const ReportAnalysisTab: React.FC = () => {
    // Get data from store
    const selectedReportId = useSelectedReportId();
    const setSelectedReport = useIRPWorkbookStore((state) => state.setSelectedReport);
    const setActiveTab = useIRPWorkbookStore((state) => state.setActiveTab);
    const reportTabCache = useReportTabCache();
    const setReportTabCache = useIRPWorkbookStore((state) => state.setReportTabCache);
    const eventSequenceData = useIRPWorkbookStore((state) => state.eventSequenceData);
    const irpDetails = useIRPWorkbookStore((state) => state.irpDetails);
    const {dataSource, isReadOnly} = useIRPContext();
    // Unlinking and the ESD editor view only exist for IRPs; component evidence is read-only.
    const isIrpScope = dataSource?.kind === 'irp';
    const storeApi = useIRPWorkbookStoreApi();

    // Local state for view mode and highlighting
    const [viewMode, setViewMode] = useState<ViewMode>('analyst');
    const [highlightedTerms, setHighlightedTerms] = useState<string[]>([]);

    // Unlink confirmation modal state
    const [unlinkModalOpen, setUnlinkModalOpen] = useState(false);

    // Descriptor unlink confirmation modal state
    const [descriptorToUnlink, setDescriptorToUnlink] = useState<IrpDescriptorTermDTO | null>(null);
    const [isUnlinkingDescriptor, setIsUnlinkingDescriptor] = useState(false);

    // Fetch report tab data when selected report changes (Task 14.1)
    useEffect(() => {
        if (!dataSource || !selectedReportId) return;
        // Use reactive reportTabCache so the effect re-fires when setIrpId clears it.
        if (reportTabCache !== null && reportTabCache.reportId === selectedReportId) return;
        dataSource.getWorkbookReportTab(selectedReportId)
            .then((data) => setReportTabCache(data))
            .catch(() => { toast.error('Failed to load report details.'); });
    }, [dataSource, selectedReportId, reportTabCache, setReportTabCache]);

    // Loading: report is selected but cache belongs to a different report (Task 14.2)
    const isLoading = !!selectedReportId && (reportTabCache === null || reportTabCache.reportId !== selectedReportId);

    // Cache is only valid when it matches the selected report
    const currentReport = (reportTabCache?.reportId === selectedReportId) ? reportTabCache : null;

    // Stable node hover callback — avoids recreating on every render
    const handleNodeHover = useCallback((excerpts: string[]): void => {
        setHighlightedTerms(excerpts);
    }, []);

    // Open unlink confirmation modal
    const handleUnlinkClick = useCallback((): void => {
        setUnlinkModalOpen(true);
    }, []);

    // Close unlink confirmation modal
    const handleUnlinkModalClose = useCallback((): void => {
        setUnlinkModalOpen(false);
    }, []);

    // Execute the actual unlink after confirmation
    const handleUnlinkConfirmed = useCallback((): void => {
        if (!selectedReportId || !irpDetails) return;

        const esdId = storeApi.getState().eventSequenceData?.id ?? 0;
        irpService.unlinkReportFromIrp(
            irpDetails.id,
            esdId,
            selectedReportId
        ).then((updatedIrp) => {
            setSelectedReport(null);
            storeApi.getState().setIrpDetails(updatedIrp);
            toast.success(`Report ${currentReport?.uniqueId || selectedReportId} unlinked successfully.`);
        }).catch(() => {
            toast.error(`Failed to unlink report ${currentReport?.uniqueId || selectedReportId}.`);
        }).finally(() => {
            handleUnlinkModalClose();
        });
    }, [irpDetails, selectedReportId, currentReport, setSelectedReport, handleUnlinkModalClose]);

    // Open descriptor unlink confirmation modal
    const handleDescriptorUnlinkClick = useCallback((descriptor: IrpDescriptorTermDTO): void => {
        setDescriptorToUnlink(descriptor);
    }, []);

    // Close descriptor unlink confirmation modal
    const handleDescriptorUnlinkModalClose = useCallback((): void => {
        setDescriptorToUnlink(null);
    }, []);

    // Execute the descriptor unlink after confirmation
    const handleDescriptorUnlinkConfirmed = useCallback((): void => {
        if (!selectedReportId || !descriptorToUnlink) return;

        const termId = descriptorToUnlink.descriptorTermId;
        const termLabel = descriptorToUnlink.term;
        setIsUnlinkingDescriptor(true);
        irpService.unlinkDescriptorTermFromReport(selectedReportId, termId)
            .then(() => {
                // Optimistically drop the term from the cached report tab so the chip
                // disappears without a reload. The link is global, so this also reflects
                // the new state for every other IRP and view that shows this report.
                const cache = storeApi.getState().reportTabCache;
                if (cache && cache.reportId === selectedReportId) {
                    setReportTabCache({
                        ...cache,
                        descriptorTerms: cache.descriptorTerms.filter(
                            (d: IrpDescriptorTermDTO): boolean => d.descriptorTermId !== termId
                        )
                    });
                }
                setHighlightedTerms([]);
                setDescriptorToUnlink(null);
                toast.success(`Descriptor "${termLabel}" unlinked from this report.`);
            })
            .catch((error: Error) => {
                toast.error(error.message || `Failed to unlink descriptor "${termLabel}".`);
            })
            .finally(() => {
                setIsUnlinkingDescriptor(false);
            });
    }, [selectedReportId, descriptorToUnlink, setReportTabCache, storeApi]);

    return (
        <Box sx={{ height: '100%', display: 'flex', overflow: 'hidden' }}>
            {/* Left Panel - Report Header + Dendrogram (66.67%) - Always visible */}
            <Box sx={{
                width: currentReport ? '66.67%' : '100%',
                borderRight: currentReport ? '1px solid' : 'none',
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column'
            }}>
                {/* Report Header and View Toggle */}
                <Box sx={{ p: 2, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        {/* Report identifier */}
                        <Box sx={{ flex: 1 }}>
                            {isLoading ? (
                                <Skeleton variant="text" width={300} height={32} />
                            ) : currentReport ? (
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        {currentReport.uniqueId || `Report #${currentReport.reportId}`}
                                    </Typography>
                                    <Chip
                                        label={currentReport.sourceName || 'Unknown'}
                                        size="small"
                                        variant="outlined"
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        {formatDate(currentReport.dateTime)}
                                    </Typography>
                                </Stack>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No report selected
                                </Typography>
                            )}
                        </Box>

                        {currentReport && isIrpScope && (
                            <Button
                                variant="outlined"
                                onClick={handleUnlinkClick}
                                size="small"
                                color="error"
                            >
                                Unlink Report
                            </Button>
                        )}

                        {/* View Mode Toggle - Only show when report is loaded */}
                        {currentReport && isIrpScope && (
                            <ButtonGroup size="small" variant="outlined">
                                <Button
                                    variant={viewMode === 'analyst' ? 'contained' : 'outlined'}
                                    onClick={(): void => setViewMode('analyst')}
                                    startIcon={<AssessmentIcon />}
                                >
                                    Analyst
                                </Button>
                                <Button
                                    variant={viewMode === 'editor' ? 'contained' : 'outlined'}
                                    onClick={(): void => setViewMode('editor')}
                                    startIcon={<EditIcon />}
                                >
                                    Editor
                                </Button>
                            </ButtonGroup>
                        )}
                    </Stack>
                </Box>

                {/* Analyst View (Dendrogram) or Editor View (Canvas) or Empty State */}
                {selectedReportId ? (
                    viewMode === 'analyst' ? (
                        <ReportMappingDendrogram
                            selectedReportId={selectedReportId}
                            nodeMappings={currentReport?.nodeMappings ?? []}
                            onNodeHover={handleNodeHover}
                        />
                    ) : (
                        <Box sx={{ flex: 1, overflow: 'hidden' }}>
                            {eventSequenceData && (
                                <EventSequenceTreeMappingViewer
                                    esv={eventSequenceData}
                                    isActive={true}
                                    updateTab={(): void => {}}
                                    irpId={irpDetails?.id || 0}
                                    reportId={selectedReportId}
                                    readonly={false}
                                />
                            )}
                        </Box>
                    )
                ) : (
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Stack alignItems="center" spacing={2}>
                            <ListAltIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                            <Typography variant="body1" color="text.secondary">
                                No report selected
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Select a report from the <strong>Overview</strong> tab to analyse it here.
                            </Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<ListAltIcon />}
                                onClick={(): void => setActiveTab('overview')}
                            >
                                Go to Overview
                            </Button>
                        </Stack>
                    </Box>
                )}
            </Box>

            {/* Right Panel - Report Details (33.33%) - Only shown when report is loaded */}
            {currentReport && (
                <Box sx={{ width: '33.33%', display: 'flex', flexDirection: 'column' }}>
                    {/* Top Section - Location and Descriptors */}
                    <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                        {/* Location Widget (Task 14.3) */}
                        <Box sx={{ mb: 2 }}>
                            <LocationWidget
                                latitude={currentReport.latitude ?? undefined}
                                longitude={currentReport.longitude ?? undefined}
                                facilityName={currentReport.facilityName ?? undefined}
                                onLocationUpdate={(): void => {}}
                                readOnly={isReadOnly}
                            />
                        </Box>

                        {/* Descriptor chips with hover highlighting (Task 14.4) */}
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {currentReport.descriptorTerms && currentReport.descriptorTerms.length > 0 ? (
                                currentReport.descriptorTerms.map((descriptor: IrpDescriptorTermDTO, index: number) => (
                                    <Tooltip
                                        key={index}
                                        title={
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                                    {descriptor.term}
                                                </Typography>
                                                <Typography variant="body2">
                                                    {descriptor.explanation || 'No explanation provided'}
                                                </Typography>
                                            </Box>
                                        }
                                        placement="top"
                                        arrow
                                    >
                                        <Chip
                                            label={descriptor.term}
                                            size="small"
                                            variant="outlined"
                                            // Unlinking a term detaches it from the report itself,
                                            // everywhere. A component's workbook is a read-only
                                            // view of what a run recorded, so it cannot offer it.
                                            onDelete={isReadOnly
                                                ? undefined
                                                : (): void => handleDescriptorUnlinkClick(descriptor)}
                                            onMouseEnter={(): void => setHighlightedTerms(descriptor.supportingPhrases ?? [])}
                                            onMouseLeave={(): void => setHighlightedTerms([])}
                                            sx={{
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    backgroundColor: '#ffeb3b',
                                                    borderColor: '#f9a825',
                                                }
                                            }}
                                        />
                                    </Tooltip>
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                    (no terms recorded)
                                </Typography>
                            )}
                        </Stack>
                    </Box>

                    {/* Bottom Section - Report Content (Task 14.5) */}
                    <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                        {currentReport.processingStatus === 'MAPPED_INCOMPLETE' && currentReport.explanation && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                {currentReport.explanation}
                            </Alert>
                        )}
                        <HighlightedNarrative
                            narrative={currentReport.content}
                            highlightPhrases={highlightedTerms}
                            typographySx={{
                                fontFamily: '"Roboto Mono", "Courier New", monospace',
                                fontSize: '0.65rem',
                                lineHeight: 1.2,
                            }}
                        />
                    </Box>
                </Box>
            )}

            {/* Unlink Confirmation Modal */}
            {selectedReportId && (
                <StrictDeleteModal
                    open={unlinkModalOpen}
                    resourceType={ResourceType.NEW_OCCURRENCE_REPORTS}
                    resourceName={currentReport?.uniqueId || `Report #${selectedReportId}`}
                    subResources="its mappings to fault tree nodes"
                    actionLabel="Unlink"
                    onClose={handleUnlinkModalClose}
                    handleDelete={handleUnlinkConfirmed}
                />
            )}

            {/* Descriptor Unlink Confirmation Modal */}
            {descriptorToUnlink && (
                <StrictDeleteModal
                    open={!!descriptorToUnlink}
                    resourceType={ResourceType.OCCURRENCE_REPORT_DESCRIPTORS}
                    resourceName={descriptorToUnlink.term}
                    subResources="its explanation and supporting phrases"
                    actionLabel="Unlink"
                    additionalWarning="This removes the descriptor from the report for ALL users and ALL IRPs, not just this one. To remove the report from this IRP instead, use the Unlink Report button."
                    isDeleting={isUnlinkingDescriptor}
                    progressMessage="Unlinking descriptor..."
                    onClose={handleDescriptorUnlinkModalClose}
                    handleDelete={handleDescriptorUnlinkConfirmed}
                />
            )}
        </Box>
    );
};

export default ReportAnalysisTab;
